import { Field, type FieldInputProps } from 'formik';
import { useAppSelector, useAppStore } from 'hooks/redux';
import type { Map as ImmutableMap } from 'immutable';
import { List } from 'immutable';
import _get from 'lodash/get';
import React, { useMemo, useState } from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import {
  chartOfAccountsSelectors,
  type chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'app-components/flux/chart-of-accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { customFrequencyUtils, scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import type {
  ScheduledTransaction,
  ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import { getCommonConfig, tracker } from 'app-components/utils/core';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';

import type { Theme } from '@mui/material/styles';
import QAmountField from 'components/QAmountField';
import AccountField from 'components/QuickenControls/AccountField';
import MuiTagsField from 'components/QuickenControls/TagsField/TagsField';
import VisibilitySection from 'components/Transactions/VisibilitySection';
import TransferExclusion from 'components/Transactions/VisibilitySection/transferExclusion';
import {
  getExcludedFromReportsDisplayState,
  getExcludedFromSpendingPlanDisplayState,
} from 'components/Transactions/VisibilitySection/utils';
import UsageWithCategory from 'components/UsageWithCategory';
import { splitItemsToArray } from 'app-components/flux/transactions/splitsHelpers';
import { applySeriesTypeChange, type SeriesType } from './typeSelection';

import { makeStyles } from 'tss-react/mui';
// import type { Im2JS } from 'types/shared';

import BillConnectButton from '../BillConnectButton';
import QFreqButton from '../QFreqButton';
import type { STFormikProps } from '../types';
import QPanelButton from 'components/QPanelButton';
import IconButton from '@mui/material/IconButton/IconButton';
import TransactionSplits from 'components/TransactionSplits';

const useStyles = makeStyles()((theme: Theme) => ({
  visibilitySectionRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexBasis: '500px',
    '& .MuiTextField-root': {
      margin: 0,
      paddingBottom: 0,
      width: '100%',
    },
  },
  tabContent: {
    width: '100%',
    display: 'flex',
    gap: theme.spacing(3),
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    gap: theme.spacing(3),
    '> div:last-of-type': {
      paddingRight: 0,
      '&.MuiButtonBase-root': {
        margin: 0,
      },
    },
  },
  width: { width: '50%' },
  amtTxt: {
    textAlign: 'left',
  },
  usageType: {
    maxWidth: 85,
  },
  accountField: {},
  bottomSection: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  bottomLeft: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 365,
    '.MuiFormControl-root': {
      width: '150px',
    },
  },
  bottomRight: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  greyText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  inputLabel: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  matchCriteriaField: {
    backgroundColor: theme.palette.background.default,
  },
  infoIcon: {
    color: theme.palette.info.main,
    marginRight: 8,
    marginTop: 6,
    cursor: 'default',
    height: '16px',
    width: '16px',
  },
  learnMore: {
    flexDirection: 'row',
    display: 'flex',
  },
  innerCriteriaItem: {
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '219px',
    marginTop: 8,
  },
  learnMoreText: {
    whiteSpace: 'break-spaces',
  },
  editSplitAmount: {
    margin: 0,
    padding: 0,
    height: 58,
    borderRadius: '12px 0px 0px 12px',
    border: `1px solid ${theme.palette.border.light}`,
    // boxShadow: '0px -1px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
}));

const matchCriteriaOptions = {
  AUTO_MATCH: 'Auto Match',
  ANY_AMOUNT: 'Any Amount',
  EXACT_AMOUNT: 'Exact Amount',
  LIMITED_RANGE: 'Limited Range',
};

interface BasicTabProps {
  formikProps: STFormikProps;
  scheduledTransaction: ScheduledTransaction | ScheduledTransactionType;
  create?: boolean;
  subscriptionInitial?: boolean;
  setTabValue: (a: number) => void;
  showBillConnect?: boolean;
  onAssignSplits?: () => void;
}

function BasicTab(props: BasicTabProps) {
  const { classes } = useStyles();
  const store = useAppStore();

  const { formikProps, scheduledTransaction, create, setTabValue, showBillConnect, onAssignSplits } = props;

  const { values, touched, errors } = formikProps;
  const { transaction } = formikProps.values || {};
  const txnAmount = transaction.amount || 0;
  const splitRecord = transaction?.split ? transactionsTypes.mkSplit(transaction.split as any) : null;
  const splitCount = splitRecord?.items ? splitItemsToArray(splitRecord.items).length : 0;
  const hasSplitsAssigned = splitCount > 0;

  const featureFlags: ImmutableMap<string, any> = useAppSelector(featureFlagsSelectors.getFeatureFlags);
  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const transactionsById = useAppSelector(transactionsSelectors.getResourcesById);
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const incomeCOAIds = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const amountMatchVarianceFF = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('matchAmountVariants'),
  );

  // const [selectedCriteria, setSelectedCriteria] = useState(amountCriteria);
  const [focus, setFocus] = useState(null);
  const [excludeF2SOverridden, setExcludeF2SOverridden] = useState(false);

  const variancePercent =
    create && amountMatchVarianceFF
      ? amountMatchVarianceFF
      : !create && values.matchCriteria === matchCriteriaOptions.LIMITED_RANGE
        ? scheduledTransaction?.matchAmountVariance
        : 15;

  const valTxn = {
    ...(scheduledTransaction?.transaction || {}),
    ...(transaction || {}),
  } as transactionsTypes.TransactionProps;
  const state = store.getState();
  const seriesPrimaryCoa = scheduledTransactionsUtils.getSeriesPrimaryCoa(valTxn);
  const hasPrimarySplitCoa = Boolean(hasSplitsAssigned && seriesPrimaryCoa);
  const splitPrimaryIsTransfer = Boolean(seriesPrimaryCoa && chartOfAccountsUtils.isTransferCOA(seriesPrimaryCoa));
  const splitPrimaryIsIncome = Boolean(
    seriesPrimaryCoa?.type === 'CATEGORY' && categoriesSelectors.isIncomeCat(state, seriesPrimaryCoa.id),
  );
  const splitPrimaryIsExpense = Boolean(hasPrimarySplitCoa && !splitPrimaryIsTransfer && !splitPrimaryIsIncome);
  const disableTypeChange = Boolean(hasSplitsAssigned && (splitPrimaryIsTransfer || splitPrimaryIsIncome));

  const isCredit = valTxn && chartOfAccountsSelectors.isCreditBill(state, valTxn.accountId, valTxn.coa);
  const isIncome = scheduledTransactionsUtils.isIncomeSeriesFromTxn(valTxn, incomeCOAIds);
  const isTransfer = Boolean(seriesPrimaryCoa && chartOfAccountsUtils.isTransferCOA(seriesPrimaryCoa)) || isCredit;
  const incomeCategory = useAppSelector(categoriesSelectors.getPersonalIncomeCategory);

  const SERIES_TYPE_LABEL: Record<SeriesType, string> = {
    BILL: 'Bill',
    SUBSCRIPTION: 'Subscription',
    TRANSFER: 'Transfer',
    INCOME: 'Income',
  };
  const selectedType: SeriesType = isTransfer
    ? 'TRANSFER'
    : isIncome
      ? 'INCOME'
      : transaction.isSubscription
        ? 'SUBSCRIPTION'
        : 'BILL';
  const selectableSeriesTypes: SeriesType[] = splitPrimaryIsExpense
    ? ['BILL', 'SUBSCRIPTION']
    : ['BILL', 'SUBSCRIPTION', 'TRANSFER', 'INCOME'];

  const rangeValue = txnAmount * (variancePercent / 100);
  const limitedRangeAmount = txnAmount
    ? `$${Math.abs(txnAmount - rangeValue).toFixed(2)} - $${Math.abs(txnAmount + rangeValue).toFixed(2)}`
    : '$0 - $0';

  const matchCriteria = [
    {
      id: 'auto_match',
      title: matchCriteriaOptions.AUTO_MATCH,
      subTitle: 'Simplifi will optimize for you',
      showSeparator: true,
    },
    {
      id: 'any_amount',
      title: matchCriteriaOptions.ANY_AMOUNT,
    },
    {
      id: 'exact_amount',
      title: matchCriteriaOptions.EXACT_AMOUNT,
    },
    {
      id: 'limited_range',
      title: matchCriteriaOptions.LIMITED_RANGE,
      subTitle: limitedRangeAmount,
      showSeparator: true,
    },
    {
      id: 'info',
      title: 'These options let you control the amount allowed to match with this series.',
      subTitle: 'Learn More >',
      image: { icon: 'info' },
    },
  ];

  // If category opening balance or balance adjustment is selected, they should be ignored and unable to be changed back.
  const disableReports = chartOfAccountsUtils.disableExcludeReports(valTxn?.coa);
  const excludeF2SDisplayFromTxn = useMemo(
    () =>
      getExcludedFromSpendingPlanDisplayState(
        {
          isExcludedFromF2S: transaction.isExcludedFromF2S,
          split: transaction.split,
        },
        transactionsById,
      ),
    [transaction.isExcludedFromF2S, transaction.split, transactionsById],
  );
  const excludeF2SCheckboxState = useMemo(() => {
    if (excludeF2SOverridden) {
      return { value: Boolean(transaction.isExcludedFromF2S), indeterminate: false };
    }
    return excludeF2SDisplayFromTxn;
  }, [excludeF2SDisplayFromTxn, excludeF2SOverridden, transaction.isExcludedFromF2S]);
  const excludeReportsDisplayFromTxn = useMemo(
    () =>
      getExcludedFromReportsDisplayState(
        {
          isExcludedFromReports: transaction.isExcludedFromReports,
          split: transaction.split,
        },
        transactionsById,
      ),
    [transaction.isExcludedFromReports, transaction.split, transactionsById],
  );
  const visibilityF2SDisplay = useMemo(
    () => (hasSplitsAssigned ? excludeF2SDisplayFromTxn : excludeF2SCheckboxState),
    [excludeF2SCheckboxState, excludeF2SDisplayFromTxn, hasSplitsAssigned],
  );
  const visibilityExcludeReportsDisplay = useMemo(
    () =>
      hasSplitsAssigned
        ? excludeReportsDisplayFromTxn
        : { value: transaction.isExcludedFromReports, indeterminate: false as const },
    [excludeReportsDisplayFromTxn, hasSplitsAssigned, transaction.isExcludedFromReports],
  );

  //

  // @ts-expect-error FIXME TS7006
  const accountValidation = (value, valTxn) => {
    let errorMessage;
    if (!value || value.length <= 0) {
      errorMessage = 'Required';
    }

    if (valTxn?.coa && valTxn?.coa?.type === 'ACCOUNT' && value === valTxn?.coa?.id) {
      errorMessage = 'No self-transfers';
    }

    return errorMessage;
  };

  // @ts-expect-error FIXME TS7006
  const coaValidation = (coa, valTxn, state) => {
    let errorMessage;

    if (!coa) {
      errorMessage = 'Required';
    }

    if (transactionsUtils.txnHasUnsupportedTransfer({ coa }, featureFlags, state)) {
      errorMessage = 'Invalid transfer target';
    }

    if (coa && coa?.type === 'ACCOUNT' && valTxn.accountId === coa?.id) {
      errorMessage = 'No self-transfers';
    }

    return errorMessage;
  };

  const updateUsageAndCategory = async ({
    usage: newUsage,
    coa: newCoa,
  }: {
    usage: string;
    coa: chartOfAccountsTypes.ChartOfAccountProps;
  }) => {
    const acc = accountsById.get(transaction?.accountId);
    const previousCoa = transaction?.coa;

    if (chartOfAccountsUtils.isTransferCOA(newCoa)) {
      formikProps.setFieldValue('transaction.isExcludedFromF2S', true);
      formikProps.setFieldValue('transaction.excludeF2SDest', true);
    } else if (!acc?.isExcludedFromBudgets && chartOfAccountsUtils.isTransferCOA(previousCoa)) {
      formikProps.setFieldValue('transaction.isExcludedFromF2S', false);
      formikProps.setFieldValue('transaction.excludeF2SDest', false);
    }

    if (chartOfAccountsUtils.isTransferCOA(newCoa)) {
      formikProps.setFieldValue('transaction.isExcludedFromReports', true);
      formikProps.setFieldValue('transaction.excludeReportsDest', true);
    } else if (!acc?.isExcludedFromReports && chartOfAccountsUtils.isTransferCOA(previousCoa)) {
      formikProps.setFieldValue('transaction.isExcludedFromReports', false);
      formikProps.setFieldValue('transaction.excludeReportsDest', false);
    }

    // bill + sub flag updates
    if (chartOfAccountsSelectors.isIncomeOrTransfer(state, newCoa)) {
      // force incomes and transfer to nothing
      formikProps.setFieldValue('transaction.isBill', false);
      formikProps.setFieldValue('transaction.isSubscription', false);

      /* if (chartOfAccountsSelectors.isCreditBill(state, valTxn.accountId, coa)) {
          formikProps.setFieldValue('transaction.isTransfer', false);
          formikProps.setFieldValue('transaction.isCredit', true);
        } else */
      if (chartOfAccountsUtils.isTransferCOA(newCoa)) {
        formikProps.setFieldValue('transaction.isTransfer', true);
        formikProps.setFieldValue('transaction.isIncome', false);
        //  formikProps.setFieldValue('transaction.isCredit', false);
      } else {
        formikProps.setFieldValue('transaction.isTransfer', false);
        formikProps.setFieldValue('transaction.isIncome', true);
      }
    } else {
      // Preserve the user's chosen Bill/Subscription type when changing category.
      const wantsSubscription = Boolean(formikProps.values?.transaction?.isSubscription);
      formikProps.setFieldValue('transaction.isTransfer', false);
      formikProps.setFieldValue('transaction.isIncome', false);
      formikProps.setFieldValue('transaction.isBill', !wantsSubscription);
      formikProps.setFieldValue('transaction.isSubscription', wantsSubscription);
    }
    if (create && transaction?.amount < 0 && categoriesSelectors.isIncomeCat(null, newCoa.id)) {
      // on create, changed to income cat on create, flip sign
      formikProps.setFieldValue('transaction.amount', Number(transaction.amount * -1));
    } else if (
      create &&
      categoriesSelectors.isIncomeCat(null, transaction.coa?.id || '') &&
      !categoriesSelectors.isIncomeCat(null, newCoa.id) &&
      transaction.amount > 0
    ) {
      // on create, changed from income to expense on create, flip sign back
      formikProps.setFieldValue('transaction.amount', Number(transaction.amount * -1));
    }
    await formikProps.setFieldValue('transaction.coa', newCoa);
    formikProps.setFieldValue('transaction.businessId', newUsage);
  };

  const setType = async (nextType: SeriesType) => {
    const account = accountsById.get(formikProps.values?.transaction?.accountId);
    await applySeriesTypeChange({
      nextType,
      create: Boolean(create),
      currentTxn: {
        coa: transaction?.coa,
        amount: transaction?.amount,
      },
      incomeCategoryId: incomeCategory?.id ?? null,
      accountExcludeDefaults: {
        excludeFromSpendingPlan: Boolean(account?.isExcludedFromBudgets),
        excludeFromReports: Boolean(account?.isExcludedFromReports),
      },
      setFieldValue: formikProps.setFieldValue,
    });
  };

  // # ===================================================================================== #
  // # -----                                Renderers                                  ----- #
  // # ===================================================================================== #

  // @ts-expect-error FIXME TS7006
  const renderDateAndFrequencySummary = (formikProps) => {
    const { virtualRecurrence, dueOn, virtualEndOn, virtualFrequency } = formikProps.values || {};
    const { text: summaryText } = customFrequencyUtils.recurrenceInfo({
      recurrence: virtualRecurrence,
      startDate: dueOn,
      endDate: virtualEndOn,
    });

    return (
      <QFreqButton
        // @ts-expect-error FIXME TS2322
        id='setDateAndFreqInitial'
        dueOn={dueOn}
        endOn={virtualEndOn}
        frequency={virtualFrequency}
        summaryText={summaryText}
        onClick={() => {
          tracker.track(tracker.events.viewFrequencyDetails);
          setTabValue(1);
        }}
      />
    );
  };

  // Main render

  return (
    <div className={classes.root}>
      <div className={classes.tabContent}>
        {/*
          // @ts-expect-error FIXME TS7006 */}
        <Field name='transaction.payee' validate={(value) => (value.length > 0 ? undefined : 'Required')}>
          {/*
            // @ts-expect-error FIXME TS7031 */}
          {({ field }) => (
            <TextField
              {...field}
              id='payee'
              label='Name'
              error={Boolean(_get(errors, field.name))}
              helperText={_get(errors, field.name)}
              InputProps={{
                autoComplete: 'off',
              }}
              variant='filled'
              margin='normal'
              fullWidth
              autoFocus
              onFocus={(event) => event.target.select()}
            />
          )}
        </Field>

        <div className={classes.row}>
          <Stack direction='row' display='flex' style={{ width: '100%' }}>
            <Field
              name='transaction.amount'
              // @ts-expect-error FIXME TS7006
              validate={(value) => (value !== undefined && value !== '' ? undefined : 'Required')}
            >
              {({ field }: { field: FieldInputProps<number | ''> }) =>
                hasSplitsAssigned ? (
                  <QPanelButton
                    id='edit-split-amount'
                    className={classes.editSplitAmount}
                    onClick={() => {
                      const amountForTracking = typeof field.value === 'number' ? field.value : 0;
                      tracker.track(tracker.events.viewSplitsV2, {
                        initiator: transaction.id === null ? 'createTxn' : 'editTxnWithSplits',
                        transactionAmount: amountForTracking,
                        transactionAmountSign: amountForTracking >= 0 ? 'positive' : 'negative',
                      });
                      onAssignSplits?.();
                    }}
                    title={
                      <QAmountField
                        value={field.value}
                        onChange={(event) =>
                          formikProps.setFieldValue(
                            field.name,
                            event.target.value === '' ? '' : Number(event.target.value),
                          )
                        }
                        expectedSign={isIncome ? 'positive' : 'negative'}
                        // editable
                        id='schedtxn-amount'
                        label='Recurring Amount'
                        fullWidth
                        // textFieldVariant='filled'
                        autoComplete='one-time-code'
                        showSign='positive'
                        showColor='positive'
                      />
                    }
                    arrow={
                      <IconButton size='small'>
                        <ChevronRightIcon fontSize='small' />
                      </IconButton>
                    }
                  />
                ) : (
                  <QAmountField
                    {...field}
                    onChange={(event) =>
                      formikProps.setFieldValue(field.name, event.target.value === '' ? '' : Number(event.target.value))
                    }
                    expectedSign={isIncome ? 'positive' : 'negative'}
                    editable
                    id='schedtxn-amount'
                    label='Recurring Amount'
                    fullWidth
                    textFieldVariant='filled'
                    margin='normal'
                    autoComplete='one-time-code'
                    error={Boolean(_get(errors, field.name))}
                    classes={{ inputField: classes.amtTxt }}
                    className={classes.width}
                    placeholder='0.00'
                    helperText={_get(errors, field.name)}
                    InputProps={{
                      sx: {
                        borderTopRightRadius: '0px',
                        borderBottomRightRadius: '0px',
                        borderRightStyle: 'none',
                      },
                    }}
                  />
                )
              }
            </Field>
            <Field name='matchCriteria'>
              {({ field: { ...field } }) => (
                <FormControl variant='filled' className={classes.width}>
                  <InputLabel className={classes.inputLabel}>Match Criteria</InputLabel>
                  <Select
                    value={field.value}
                    autoFocus={focus === field.name}
                    onChange={(event) => {
                      if (
                        event.target.value !==
                        'These options let you control the amount allowed to match with this series.'
                      ) {
                        formikProps.setFieldValue('matchCriteria', event.target.value);
                        // setSelectedCriteria(event.target.value as string);
                      }
                      if (event.target.value === matchCriteriaOptions.AUTO_MATCH) {
                        formikProps.setFieldValue('resetMatchAmountVariance', true);
                        formikProps.setFieldValue('matchAnyAmount', false);
                      }
                      if (event.target.value === matchCriteriaOptions.ANY_AMOUNT) {
                        formikProps.setFieldValue('matchAnyAmount', true);
                        formikProps.setFieldValue('resetMatchAmountVariance', true);
                      }
                      if (event.target.value === matchCriteriaOptions.EXACT_AMOUNT) {
                        formikProps.setFieldValue('matchAmountVariance', 0);
                        formikProps.setFieldValue('resetMatchAmountVariance', false);
                        formikProps.setFieldValue('matchAnyAmount', false);
                      }
                      if (event.target.value === matchCriteriaOptions.LIMITED_RANGE) {
                        formikProps.setFieldValue('matchAmountVariance', variancePercent);
                        formikProps.setFieldValue('resetMatchAmountVariance', false);
                        formikProps.setFieldValue('matchAnyAmount', false);
                      }

                      setFocus(field.name);
                    }}
                    sx={{
                      borderTopLeftRadius: '0px',
                      borderBottomLeftRadius: '0px',
                    }}
                    renderValue={() => <Typography variant='bodyRegular'>{field.value}</Typography>}
                    className={classes.matchCriteriaField}
                  >
                    {matchCriteria.map((item) => {
                      return (
                        <MenuItem key={item.id} value={item.title} divider={item.showSeparator}>
                          {item.id === 'info' ? (
                            <div className={classes.innerCriteriaItem}>
                              <div className={classes.learnMore}>
                                <InfoIcon fontSize='small' className={classes.infoIcon} />
                                <div>
                                  <Typography variant='captionRegular' className={classes.learnMoreText}>
                                    These options let you control the amount allowed to match with this series.
                                  </Typography>
                                  <br />
                                  <Typography
                                    variant='captionRegular'
                                    id='learn_more'
                                    color='primary'
                                    style={{ textTransform: 'capitalize' }}
                                    onClick={() => {
                                      window.open(getCommonConfig()?.external?.recurring_transactions);
                                    }}
                                  >
                                    Learn More {'>'}
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <Typography variant='bodyRegular'>{item.title}</Typography>
                              {item.subTitle && (
                                <Typography variant='paragraphRegular' color={'textSecondary'}>
                                  {item.subTitle}
                                </Typography>
                              )}
                            </div>
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Field>
          </Stack>

          {/*
            // @ts-expect-error FIXME TS7006 */}
          <Field name='transaction.accountId' validate={(value) => accountValidation(value, valTxn)}>
            {({ field: { ...field } }) => (
              <AccountField
                {...field}
                key={_get(values, field.name)}
                // @ts-expect-error FIXME TS7006
                onSelected={(account) => {
                  if (account) {
                    // bugsnag check
                    const previousAcc = accountsById.get(transaction?.accountId);

                    if (account.isExcludedFromReports) {
                      formikProps.setFieldValue('transaction.isExcludedFromReports', true);
                      formikProps.setFieldValue('transaction.excludeReportsDest', true);
                    } else if (
                      !chartOfAccountsUtils.isTransferCOA(transaction.coa) &&
                      previousAcc?.isExcludedFromReports
                    ) {
                      formikProps.setFieldValue('transaction.isExcludedFromReports', false);
                      formikProps.setFieldValue('transaction.excludeReportsDest', false);
                    }
                    if (account.isExcludedFromBudgets) {
                      formikProps.setFieldValue('transaction.isExcludedFromF2S', true);
                      formikProps.setFieldValue('transaction.excludeF2SDest', true);
                    } else if (
                      !chartOfAccountsUtils.isTransferCOA(transaction.coa) &&
                      previousAcc?.isExcludedFromBudgets
                    ) {
                      formikProps.setFieldValue('transaction.isExcludedFromF2S', false);
                      formikProps.setFieldValue('transaction.excludeF2SDest', false);
                    }

                    if (!touched.transaction?.isBill && !touched.transaction?.isSubscription) {
                      if (chartOfAccountsSelectors.isBill(state, account.id, valTxn && valTxn.coa)) {
                        if (!valTxn.isSubscription) {
                          formikProps.setFieldValue('transaction.isBill', true);
                          formikProps.setFieldValue('transaction.isSubscription', false);
                        }
                      }
                    }
                    setFocus(field.name);
                    formikProps.setFieldValue(field.name, account ? account.id : undefined);
                  }
                }}
                initialAccountId={_get(values, field.name)}
                autoFocus={focus === field.name}
                id='account'
                label='Select Account'
                margin='normal'
                vairant='filled'
                autoComplete='one-time-code'
                fullWidth
                doNotIncludeClosed
                error={Boolean(_get(errors, field.name))}
                helperText={_get(errors, field.name)}
                className={classes.accountField}
              />
            )}
          </Field>
        </div>
        <div>
          {/* If there are no splits assigned, show the Category and Tags fields, otherwise dont show them */}
          {splitSeriesFf && hasSplitsAssigned ? null : (
            <div className={classes.row}>
              <Field name='transaction' validate={(transaction: any) => coaValidation(transaction.coa, valTxn, state)}>
                {/*
                // @ts-expect-error FIXME TS7031 */}
                {({ field }) => {
                  const isError = Boolean(_get(touched, field.name) && _get(errors, field.name));
                  let helperText = '';
                  if (isError) {
                    helperText = typeof _get(errors, field.name) === 'string' ? _get(errors, field.name) : '';
                  }
                  return (
                    <UsageWithCategory
                      {...field}
                      accountId={field.value.accountId}
                      categoryProps={{
                        textFieldVariant: 'filled',
                        width: '260px',
                        label: 'Category',
                        editable: true,
                        value: field.value.coa,
                        error: Boolean(_get(touched, field.name) && _get(errors, field.name)),
                        helperText: helperText,
                      }}
                      onChange={updateUsageAndCategory}
                      usageProps={{
                        className: classes.usageType,
                        value: field.value.businessId,
                      }}
                    />
                  );
                }}
              </Field>

              <Field name='transaction.tags'>
                {/*
                  // @ts-expect-error FIXME TS7031 */}
                {({ field }) => (
                  <MuiTagsField
                    {...field}
                    value={Array.isArray(transaction?.tags) ? transaction?.tags : transaction?.tags?.toArray() || []}
                    id='transaction-tags'
                    onChange={(_e, tagArray, _reason, _details) =>
                      formikProps.setFieldValue(field.name, List(tagArray))
                    }
                  />
                )}
              </Field>
            </div>
          )}

          {splitSeriesFf ? (
            <TransactionSplits
              transaction={valTxn}
              onAssignSplits={onAssignSplits}
              variant='series'
              includeOverflowSplitLine
            />
          ) : null}
        </div>
        {renderDateAndFrequencySummary(formikProps)}
        {showBillConnect ? <BillConnectButton onClick={() => setTabValue(2)} formikProps={formikProps} /> : null}

        <Divider />

        <div className={classes.bottomSection}>
          <div className={classes.bottomLeft}>
            <Typography variant='paragraphRegular' className={classes.greyText}>
              Type
            </Typography>

            <Tooltip
              title={
                disableTypeChange
                  ? "Type can't be changed for split series. It's derived from the first split line."
                  : ''
              }
              arrow
              placement='top'
              disableHoverListener={!disableTypeChange}
            >
              <span>
                <FormControl variant='filled' size='small' hiddenLabel>
                  <Select
                    id='series-type-select'
                    value={selectedType}
                    onChange={(event) => setType(event.target.value as SeriesType)}
                    className={classes.matchCriteriaField}
                    renderValue={(value) => (
                      <Typography variant='bodyRegular'>{SERIES_TYPE_LABEL[value as SeriesType]}</Typography>
                    )}
                    inputProps={{ 'aria-label': 'Type' }}
                    size='small'
                    disabled={disableTypeChange}
                  >
                    {selectableSeriesTypes.map((seriesTypeOption) => (
                      <MenuItem key={seriesTypeOption} value={seriesTypeOption}>
                        {SERIES_TYPE_LABEL[seriesTypeOption]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </span>
            </Tooltip>
          </div>

          <div className={classes.bottomRight}>
            {valTxn?.coa?.type === 'ACCOUNT' ? (
              <TransferExclusion
                disabledReports={disableReports}
                excludeF2S={transaction.isExcludedFromF2S}
                setExcludeF2S={() =>
                  formikProps.setFieldValue('transaction.isExcludedFromF2S', !transaction.isExcludedFromF2S)
                }
                excludeReports={transaction.isExcludedFromReports}
                setExcludeReports={() =>
                  formikProps.setFieldValue('transaction.isExcludedFromReports', !transaction.isExcludedFromReports)
                }
                excludeReportsDest={transaction.excludeReportsDest}
                setExcludeReportsDest={() =>
                  formikProps.setFieldValue('transaction.excludeReportsDest', !transaction.excludeReportsDest)
                }
                excludeF2SDest={transaction.excludeF2SDest}
                setExcludeF2SDest={() =>
                  formikProps.setFieldValue('transaction.excludeF2SDest', !transaction.excludeF2SDest)
                }
              />
            ) : (
              <VisibilitySection
                className={classes.visibilitySectionRoot}
                disabledReports={disableReports}
                excludeF2S={visibilityF2SDisplay.indeterminate ? null : visibilityF2SDisplay.value}
                indeterminateF2S={visibilityF2SDisplay.indeterminate}
                setExcludeF2S={() => {
                  setExcludeF2SOverridden(true);
                  formikProps.setFieldValue('transaction.isExcludedFromF2S', !(visibilityF2SDisplay.value ?? false));
                }}
                excludeReports={
                  disableReports
                    ? true
                    : visibilityExcludeReportsDisplay.indeterminate
                      ? null
                      : visibilityExcludeReportsDisplay.value
                }
                setExcludeReports={() =>
                  formikProps.setFieldValue('transaction.isExcludedFromReports', !transaction.isExcludedFromReports)
                }
                indeterminateReports={visibilityExcludeReportsDisplay.indeterminate}
                managedPerSplit={hasSplitsAssigned}
                onEditSplits={hasSplitsAssigned ? onAssignSplits : undefined}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

BasicTab.displayName = 'stBasicTab';

export default BasicTab;
